import { useEffect, useState, useRef } from 'react';
import { Box, Label } from '@nimbus-ds/components';
import { SearchIcon } from '@nimbus-ds/icons';
import { Button, Input, Stack } from '@tiendanube/components';
import ModalAside from 'App/components/lab/ModalAside';
import { FEATURE_SEARCH_FILTER } from 'App/features';
import { DateFilter } from 'commons/components';
import MultiSelect from 'commons/components/MultiSelect';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import SearchMultiSelect from 'commons/components/SearchMultiSelect';
import { ORDERS_SHOW_FILTERS } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import {
  OrderApplicationsFilter,
  OrderPaymentMethodsFilter,
} from 'domains/Orders/components';
import OrderPaymentProvidersFilter from 'domains/Orders/components/OrderPaymentProvidersFilter';
import { ProductInterface } from 'domains/Orders/components/ProductSearcher';
import ProductsFilter from 'domains/Orders/components/ProductsFilter';
import { ProductsFilterValue } from 'domains/Orders/components/ProductsFilter/ProductsFilter';
import useGetOrdersFilters from 'domains/Orders/Orders/hooks/useGetOrdersFilters/useGetOrdersFilters';
import useIsProductsFilterEnabled from 'domains/Orders/Orders/hooks/useIsProductsFilterEnabled';
import useOrderCouponSearchOptions from 'domains/Orders/Orders/hooks/useOrderCouponSearchOptions/useOrderCouponSearchOptions';
import {
  ExportFiltersType,
  fulfillmentStatusFilter,
  paymentMethodsFilter,
  paymentProviderFilter,
  paymentStatusFilter,
  shippingMethodFilter,
} from 'domains/Orders/Orders/ordersService';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useOrdersExportFilters } from '../../hooks';

interface OrdersExportFiltersModalProps {
  show: boolean;
  filters: ExportFiltersType;
  onConfirm: (
    filters: ExportFiltersType,
    products: ProductInterface<true>[],
  ) => void;
  onClose: () => void;
  selectedProducts: ProductInterface<true>[];
  serializeProducts: (products: ProductInterface<true>[]) => string;
  onCouponOptionsLabel: (options: OptionType[]) => void;
}

function OrdersExportFiltersModal({
  show,
  filters,
  onConfirm,
  onClose,
  selectedProducts,
  serializeProducts,
  onCouponOptionsLabel,
}: OrdersExportFiltersModalProps) {
  const t = useTranslationOrders();
  const {
    values,
    fulfillmentStatusOptions,
    paymentOptions,
    paymentMethodOptionsForMultiSelect,
    paymentProviderOptionsForMultiSelect,
    defaultStatus,
    statusOptionsForMultiSelect,
    defaultShippingMethod,
    shippingMethodOptionsForMultiSelect,
    defaultFulfillmentStatus,
    defaultPaymentStatus,
    defaultPaymentMethod,
    defaultPaymentProvider,
    defaultOrigin,
    originOptionsForMultiSelect,
    handleChange,
    handleDateRangeChange,
    handleMultiSelectChange,
    setAllValues,
    applicationOptions,
  } = useOrdersExportFilters(filters);
  const { fetchOrdersFilters } = useGetOrdersFilters();
  const hasProductsFilter = useIsProductsFilterEnabled();

  const [selectedProductsValues, setSelectedProductsValues] =
    useState(selectedProducts);

  const handleConfirm = () => {
    onConfirm(values, selectedProductsValues);
    onCouponOptionsLabel(defaultCouponOptions);
    onClose();
  };

  const handleCancel = () => {
    setAllValues(filters);
    onClose();
  };

  const {
    options: couponOptions,
    onInputChange,
    getDefaultCouponValues,
    loading,
  } = useOrderCouponSearchOptions();
  const [defaultCouponOptions, setDefaultCouponOptions] = useState<
    OptionType[]
  >([]);
  const hasInitializedCoupons = useRef(false);

  const handleProductsFilterChange = useUpsellFlow({
    title: t('filter.upsell.openFilters'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SHOW_FILTERS,
    asAside: true,
    callback: (value: ProductsFilterValue) => {
      setSelectedProductsValues(value.products);
      const serializedProducts = serializeProducts(value.products);
      const newValues = {
        products: serializedProducts,
        exactProductSet: value.exactProductSet,
      };
      setAllValues({ ...values, ...newValues });
    },
  });

  useEffect(() => {
    const resolveMissingCouponNames = async () => {
      try {
        const resolved = await getDefaultCouponValues(values.couponIdsRaw);
        setDefaultCouponOptions(resolved);
        if (!hasInitializedCoupons.current) {
          onCouponOptionsLabel(resolved);
          hasInitializedCoupons.current = true;
        }
      } catch {
        setDefaultCouponOptions([]);
      }
    };

    if (values.couponIdsRaw) {
      resolveMissingCouponNames();
    } else {
      setDefaultCouponOptions([]);
    }
  }, [values.couponIdsRaw, onCouponOptionsLabel, getDefaultCouponValues]);

  useEffect(() => {
    setAllValues(filters);
  }, [filters, setAllValues]);

  useEffect(() => {
    fetchOrdersFilters();
  }, [fetchOrdersFilters]);

  useEffect(() => {
    setSelectedProductsValues(selectedProducts);
  }, [selectedProducts]);

  return (
    <ModalAside
      isOpen={show}
      onDidDismiss={onClose}
      headerContent={t('exportPage.filtersTitle')}
      footer={
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          width="100%"
          gap="4"
          flex="1"
        >
          <Button onClick={handleCancel}>{`${t('filter.cancel')}`}</Button>
          <Button onClick={handleConfirm} appearance="primary">
            {`${t('filter.filter')}`}
          </Button>
        </Box>
      }
      footerHeight="70px"
    >
      <Stack column align="stretch">
        <Stack.Item>
          <Input
            name="q"
            label={t('filter.search')}
            placeholder={t('filter.searchPlaceholder')}
            value={values.q}
            onChange={handleChange}
          />
        </Stack.Item>
        <Box display="flex" flexDirection="column" gap="2">
          <Label>{t('filter.status')}</Label>
          <MultiSelect
            key={`status-${values.status}`}
            defaultValue={defaultStatus}
            options={statusOptionsForMultiSelect}
            placeholder={t('filters.status.all')}
            onChange={(selected) => handleMultiSelectChange('status', selected)}
          />
        </Box>
        <Stack.Item>
          <DateFilter
            label={t('filter.dateRange')}
            dateFrom={values.dateFrom}
            dateTo={values.dateTo}
            onChange={handleDateRangeChange}
          />
        </Stack.Item>
        <Box display="flex" flexDirection="column" gap="2">
          <Label>{t('filter.paymentStatus')}</Label>
          <MultiSelect
            defaultValue={defaultPaymentStatus}
            options={paymentOptions as OptionType[]}
            placeholder={t('filters.payment.all')}
            onChange={(selected) =>
              handleMultiSelectChange(paymentStatusFilter, selected)
            }
          />
        </Box>
        <Box display="flex" flexDirection="column" gap="2">
          <Label>{t('filter.fulfillmentStatus')}</Label>
          <MultiSelect
            defaultValue={defaultFulfillmentStatus}
            options={fulfillmentStatusOptions as OptionType[]}
            placeholder={t('filters.fulfillment.all')}
            onChange={(selected) =>
              handleMultiSelectChange(fulfillmentStatusFilter, selected)
            }
          />
        </Box>
        <Box display="flex" flexDirection="column" gap="2">
          <Label>{t('filter.shippingMethod')}</Label>
          <MultiSelect
            defaultValue={defaultShippingMethod}
            options={shippingMethodOptionsForMultiSelect}
            placeholder={t('filters.shippingMethod.all')}
            onChange={(selected) =>
              handleMultiSelectChange(shippingMethodFilter, selected)
            }
          />
        </Box>
        {hasProductsFilter && (
          <ProductsFilter
            selectedProducts={selectedProductsValues}
            exactProductSet={values.exactProductSet?.toLowerCase() === 'true'}
            onProductsFilterChange={handleProductsFilterChange}
          />
        )}
        <OrderPaymentMethodsFilter
          defaultValue={defaultPaymentMethod}
          paymentMethodOptions={paymentMethodOptionsForMultiSelect}
          onChange={(selected) =>
            handleMultiSelectChange(paymentMethodsFilter, selected)
          }
        />
        <OrderPaymentProvidersFilter
          defaultValue={defaultPaymentProvider}
          paymentProviderOptions={paymentProviderOptionsForMultiSelect}
          onChange={(selected) =>
            handleMultiSelectChange(paymentProviderFilter, selected)
          }
        />
        <Box display="flex" flexDirection="column" gap="2">
          <Label>{t('filter.origin')}</Label>
          <MultiSelect
            defaultValue={defaultOrigin}
            options={originOptionsForMultiSelect}
            placeholder={t('filters.origin.all')}
            onChange={(selected) => handleMultiSelectChange('origin', selected)}
          />
        </Box>
        <Box display="flex" flexDirection="column" gap="2">
          <Box display="flex" alignItems="center" gap="2">
            <Label>{t('filter.coupons.title')}</Label>
          </Box>
          <SearchMultiSelect
            options={couponOptions}
            defaultValue={defaultCouponOptions}
            placeholder={t('filter.coupons.inputPlaceholder')}
            onChange={(selected) =>
              handleMultiSelectChange('couponIdsRaw', selected)
            }
            onInputChange={onInputChange}
            noOptionsMessage={() => t('filter.coupons.noResults')}
            leftIcon={SearchIcon}
            loading={loading}
            minSearchLength={2}
          />
        </Box>
        <OrderApplicationsFilter
          application={values.appId}
          applicationOptions={applicationOptions}
          handleChange={handleChange}
        />
      </Stack>
    </ModalAside>
  );
}

export default OrdersExportFiltersModal;
